import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { catchError, delay, EMPTY, of, Subscription, switchMap, tap } from 'rxjs';

import { CreateTogoRequest, MergedCustomFields } from '@shared/party/models';
import { isUsOrCaPhoneNumber, useWhatsApp } from '@shared/sms/functions';
import { Restaurant } from '@shared/restaurant/models';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { extractPartyFieldsFromCustomer } from '@shared/customer/functions';
import { TelService, ValidationService } from '@tablesready/common-ui';
import { CustomersService } from '../../../customers/services';

@Component({
  selector: 'tablesready-add-edit-togo-modal',
  templateUrl: './add-edit-togo-modal.component.html',
  styleUrls: ['./add-edit-togo-modal.component.scss'],
})
export class AddEditTogoModalComponent implements OnInit, OnDestroy {
  @Input() title = 'Add To go';
  @Input() user: Restaurant;
  @Input() onSave?: (payload: CreateTogoRequest) => void;
  @Input() initialValues: Record<string, unknown> = {};
  noteOptions: string[] = [];
  icon = AppIcon;

  form: FormGroup;
  private readonly subscriptions = new Subscription();
  private readonly shortestPhoneDigits = 4;

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly formBuilder: FormBuilder,
    private readonly validationService: ValidationService,
    private readonly telService: TelService,
    private readonly customerService: CustomersService,
  ) { }

  get partyName(): AbstractControl {
    return this.form.get('name');
  }

  get partyPhone(): AbstractControl {
    return this.form.get('phone');
  }

  get partyNote(): AbstractControl {
    return this.form.get('note');
  }

  get quotedTime(): AbstractControl {
    return this.form.get('quoted_time');
  }

  applyInitialValues(): void {
    Object.entries(this.initialValues).forEach(([key, value]) => {
      const control = this.form.get(key);
      if (!control) {
        return;
      }

      if (key === 'phone') {
        value = this.telService.format(value as string, this.user.country, 'custom');
        value && !isUsOrCaPhoneNumber(value as string) ? this.form.get('messagingProvider').enable() : this.form.get('messagingProvider').disable();
      }

      control.setValue(value);
    });
    this.form.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.noteOptions = this.user?.noteOptions || [];
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      phone: ['', [this.validationService.phonePatternCountryValidator(this.user.country)]],
      note: [''],
      quoted_time: [0, [Validators.required]],
      messagingProvider: [null, []],
      customFields: [{}],
    });
    this.applyInitialValues();
    const phoneFormattingSubscription = this.form.get('phone').valueChanges
      .pipe(
        delay(100),
        switchMap(phone => {
          if (phone) {
            const parsedPhone = this.telService.format(phone, this.user.country, 'intl');
            if (typeof parsedPhone !== 'undefined' && parsedPhone.trim().length >= this.shortestPhoneDigits) {
              const formattedPhone = this.telService.format(phone, this.user.country);
              this.form.get('phone').setValue(formattedPhone, { onlySelf: true, emitEvent: false });
              if (isUsOrCaPhoneNumber(phone)) {
                this.form.get('messagingProvider').setValue(null, {onlySelf: true, emitEvent: true});
                this.form.get('messagingProvider').disable();
              } else {
                this.form.get('messagingProvider').enable();
              }

              return this.customerService.getByPhone(parsedPhone)
                .pipe(
                  tap(data => {
                    if (data) {
                      const customerCustomFields: MergedCustomFields = extractPartyFieldsFromCustomer(this.user.partyFields, data);
                      this.form.patchValue({
                        name: this.form.value.name || customerCustomFields.name,
                        customFields: customerCustomFields.customFields,
                      });
                    }
                  }),
                  catchError(() => of(EMPTY)),
                )
            }

            this.form.get('messagingProvider').setValue(null, {onlySelf: true, emitEvent: true});
            this.form.get('messagingProvider').disable();
          } else {
            this.form.get('messagingProvider').setValue(null, {onlySelf: true, emitEvent: true});
            this.form.get('messagingProvider').disable();
          }

          return of(EMPTY);
        }),
      )
      .subscribe();
    this.subscriptions.add(phoneFormattingSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  confirm(): void {
    this.validationService.validateForm(this.form as FormGroup);
    if (this.form.invalid) {
      return;
    }

    this.modalController.dismiss<CreateTogoRequest>({
      name: this.partyName?.value,
      phone: this.telService.format(this.partyPhone?.value, this.user.country, 'intl'),
      note: this.partyNote?.value,
      quoted_time: this.quotedTime.value,
      messagingProvider: this.form.get('messagingProvider').value,
      customFields: this.form.get('customFields').value,
    });
  }

  cancel(): void {
    this.modalController.dismiss();
  }

  changeQuoted(num: number): void {
    this.quotedTime.setValue(this.quotedTime.value + num);
  }

  isWhatsAppEnabled() {
    return useWhatsApp(this.user);
  }
}
